import { useCallback, useEffect, useState } from "react";
import type { RouteId } from "../api/types.js";
import { consolePath, stripConsoleBase } from "./brand.js";

export interface AppRoute {
  id: RouteId | "not-found";
  path: string;
  title: string;
  /** Raw `location.search` (e.g. "?scanId=abc"), empty string when absent. */
  search: string;
}

const SCAN_SCOPED_PATHS = new Set([
  "/overview",
  "/findings",
  "/coverage",
  "/inventory",
  "/reports",
]);
const ALL_SCANS_SCOPE = "all";
export const DEFAULT_LANDING_REPOSITORY_NAME = "Aegis Payments Platform";

export function isScanScopedPath(pathname: string): boolean {
  return SCAN_SCOPED_PATHS.has(pathname);
}

export function latestScanId(
  scans: readonly { id: string; startedAt: string }[] | undefined,
): string | undefined {
  if (!scans?.length) return undefined;
  return [...scans].sort((left, right) => right.startedAt.localeCompare(left.startedAt))[0]?.id;
}

export function preferredLandingScanId(
  scans: readonly { id: string; startedAt: string; repositoryName?: string }[] | undefined,
): string | undefined {
  const preferred = scans?.filter(
    (scan) => scan.repositoryName === DEFAULT_LANDING_REPOSITORY_NAME,
  );
  return latestScanId(preferred?.length ? preferred : scans);
}

export function scanScopeFromSearch(search: string): {
  scanId: string | undefined;
  allScans: boolean;
} {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  if (params.get("scanScope") === ALL_SCANS_SCOPE) return { scanId: undefined, allScans: true };
  return { scanId: params.get("scanId") ?? undefined, allScans: false };
}

/** Accept only same-origin application paths supplied by reporting APIs. */
export function safeConsoleHref(value: string | null): string | null {
  if (!value?.startsWith("/") || value.startsWith("//")) return null;
  return consolePath(value);
}

/** Pending-decision queue. Default actionable preset is the work that still needs a verdict. */
export function pendingFindingsHref(scanId?: string | null): string {
  const params = new URLSearchParams({ preset: "actionable" });
  if (scanId) params.set("scanId", scanId);
  return `/findings?${params.toString()}`;
}

/** Open one finding in the full queue; hash selects the inspector. */
export function findingRecordHref(findingId: string, scanId?: string | null): string {
  const params = new URLSearchParams({ preset: "all", statuses: "" });
  if (scanId) params.set("scanId", scanId);
  return `/findings?${params.toString()}#${encodeURIComponent(findingId)}`;
}

/** Coverage cells that are never reviewed. Hash lands on the file list when present. */
export function coverageGapsHref(scanId?: string | null): string {
  const params = new URLSearchParams();
  if (scanId) params.set("scanId", scanId);
  const search = params.size > 0 ? `?${params.toString()}` : "";
  return `/coverage${search}#coverage-gaps-heading`;
}

/** Preserve the explicit scan scope only on surfaces whose API contracts consume it. */
export function pathWithScanScope(path: string, scanId?: string, allScans = false): string {
  const url = new URL(path, "https://q2.invalid");
  if (!SCAN_SCOPED_PATHS.has(url.pathname)) return `${url.pathname}${url.search}${url.hash}`;
  if (allScans) {
    url.searchParams.delete("scanId");
    url.searchParams.set("scanScope", ALL_SCANS_SCOPE);
  } else if (scanId) {
    url.searchParams.set("scanId", scanId);
    url.searchParams.delete("scanScope");
  } else {
    url.searchParams.delete("scanId");
    url.searchParams.delete("scanScope");
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

const ROUTES: Record<string, Omit<AppRoute, "search">> = {
  "/": { id: "landing", path: "/", title: "Provenance" },
  "/overview": { id: "overview", path: "/overview", title: "Overview" },
  "/findings": { id: "findings", path: "/findings", title: "Findings" },
  "/coverage": {
    id: "coverage",
    path: "/coverage",
    title: "Estate and coverage",
  },
  "/inventory": {
    id: "inventory",
    path: "/inventory",
    title: "Supply-chain assurance",
  },
  "/reports": { id: "reports", path: "/reports", title: "Reports" },
  "/scans": { id: "scans", path: "/scans", title: "Scans" },
  "/sources": { id: "sources", path: "/sources", title: "Sources" },
  "/audit": { id: "audit", path: "/audit", title: "Audit trail" },
  "/delivery": { id: "delivery", path: "/delivery", title: "GitHub delivery" },
  "/remediation": {
    id: "remediation",
    path: "/remediation",
    title: "Remediation",
  },
  "/threat-models": {
    id: "threat-models",
    path: "/threat-models",
    title: "Threat models",
  },
  "/settings": { id: "settings", path: "/settings", title: "Model providers" },
  "/admin": { id: "admin", path: "/admin", title: "Settings" },
  "/help": { id: "help", path: "/help", title: "Support" },
};

const SETTINGS_ROUTE = ROUTES["/settings"] as Omit<AppRoute, "search">;

/**
 * Exported so the route table can be asserted directly rather than through
 * `window.location`, which a static-markup test has no way to set.
 *
 * The console root carries one special case. A host-OAuth callback finishes by
 * redirecting the browser to `/?model_oauth=...&provider=...` -- deliberately
 * carrying the provider's name and nothing else, since a `Location` header ends
 * up in browser history, in a referrer and in every proxy log on the way. That
 * lands on `/`, which is otherwise the findings page, so the operator would be
 * returned to a screen with no relationship to the flow they just completed.
 * Routing it to the surface that started the flow is the whole fix.
 *
 * The parameter's *value* is read by the settings page rather than assumed:
 * arriving here is not by itself evidence the connection succeeded.
 */
export function resolveRoute(pathname: string, search: string): AppRoute {
  const consoleRoot = pathname === "/app" || pathname === "/app/";
  if (consoleRoot) pathname = "/";
  pathname = stripConsoleBase(pathname);
  const normalized = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
  if (normalized === "/" && new URLSearchParams(search).has("model_oauth")) {
    return { ...SETTINGS_ROUTE, search };
  }
  if (consoleRoot) return { ...ROUTES["/overview"]!, search };
  const base = ROUTES[normalized] ?? {
    id: "not-found" as const,
    path: normalized,
    title: "Page not found",
  };
  return { ...base, search };
}

export function useAppRoute(): {
  route: AppRoute;
  navigate: (path: string) => void;
} {
  const [route, setRoute] = useState(() =>
    resolveRoute(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const handlePopState = () =>
      setRoute(resolveRoute(window.location.pathname, window.location.search));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = useCallback((path: string) => {
    const url = new URL(consolePath(path), window.location.origin);
    const target = url.pathname + url.search + url.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (current === target) return;
    window.history.pushState({}, "", target);
    setRoute(resolveRoute(url.pathname, url.search));
    const anchor = url.hash ? document.getElementById(decodeURIComponent(url.hash.slice(1))) : null;
    if (anchor) anchor.scrollIntoView({ behavior: "instant", block: "start" });
  }, []);

  return { route, navigate };
}

export function handleInternalNavigation(
  event: React.MouseEvent<HTMLAnchorElement>,
  navigate: (path: string) => void,
): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  navigate(
    `${event.currentTarget.pathname}${event.currentTarget.search}${event.currentTarget.hash}`,
  );
}
